function formatWhen(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function NexusChatSidebar({
  sessions = [],
  activeSessionId = "",
  loading = false,
  loadingSessionId = "",
  onSelect,
  onNewChat,
  onClose,
  disabled = false,
  variant = "default",
  mode = "inline",
  open = true,
}) {
  const isLanding = variant === "landing";
  const isDrawer = mode === "drawer";
  const isNewChat = !activeSessionId && !loadingSessionId;

  if (isDrawer && !open) return null;

  return (
    <aside
      className={`nexus-chat-sidebar flex flex-col min-h-0 ${
        isDrawer
          ? "nexus-chat-sidebar--drawer"
          : isLanding
            ? "nexus-chat-sidebar--landing"
            : "nexus-chat-sidebar--dock"
      }`}
      aria-label="Conversation history"
    >
      {isDrawer ? (
        <div className="nexus-chat-sidebar-drawer-head shrink-0 flex items-center justify-between gap-2 px-3 py-2.5 border-b border-slate-200/90">
          <p className="text-xs font-semibold nexus-sidebar-title">History</p>
          <button
            type="button"
            onClick={onClose}
            className="nexus-chat-sidebar-close w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
            aria-label="Close history"
          >
            ×
          </button>
        </div>
      ) : null}

      <div className="nexus-chat-sidebar-head shrink-0 p-3 border-b border-inherit">
        <button
          type="button"
          onClick={() => {
            onNewChat?.();
            if (isDrawer) onClose?.();
          }}
          disabled={disabled}
          className={`nexus-chat-new-btn w-full flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition-all disabled:opacity-50 ${
            isNewChat ? "nexus-chat-new-btn--active" : ""
          }`}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          New chat
        </button>
      </div>

      {!isDrawer ? (
        <div className="nexus-chat-sidebar-label px-3 pt-3 pb-1 shrink-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider nexus-sidebar-muted">History</p>
        </div>
      ) : null}

      <div className="nexus-chat-sidebar-list flex-1 min-h-0 overflow-y-auto px-2 pb-2">
        {loading && !sessions.length ? (
          <p className="px-2 py-3 text-xs nexus-sidebar-muted">Loading…</p>
        ) : null}
        {!loading && !sessions.length ? (
          <p className="px-2 py-3 text-xs nexus-sidebar-muted leading-relaxed">
            Past chats appear here after you send a message.
          </p>
        ) : null}
        <ul className="space-y-1">
          {sessions.map((session) => {
            const sid = session.session_id;
            const active = sid === activeSessionId;
            const busy = loadingSessionId === sid;
            const line =
              (session.summary || session.preview || session.title || "").trim() ||
              "Conversation";
            return (
              <li key={sid}>
                <button
                  type="button"
                  disabled={disabled || busy}
                  onClick={() => {
                    onSelect?.(sid);
                    if (isDrawer) onClose?.();
                  }}
                  className={`nexus-chat-history-item w-full text-left rounded-lg px-2.5 py-2 transition-colors disabled:opacity-50 ${
                    active ? "nexus-chat-history-item--active" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-1.5">
                    <p className="text-xs font-medium leading-snug nexus-sidebar-item-title line-clamp-2">
                      {line}
                    </p>
                    <span className="text-[10px] shrink-0 tabular-nums nexus-sidebar-muted">
                      {busy ? "…" : formatWhen(session.updated_at)}
                    </span>
                  </div>
                  {session.message_count > 2 ? (
                    <p className="text-[10px] nexus-sidebar-muted mt-1">
                      {session.message_count} messages
                    </p>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </aside>
  );
}
